/**
 * PATH: packages/email-hub/src/feature/compose/dockSize.ts
 * FILE_ID: EMAILHUB-FEATURE-COMPOSE-DOCKSIZE-001
 * PURPOSE: Pure I→O sizing math for the floating compose dock — defaults,
 *          clamping to min/max + viewport, drag-resize from a top-left handle,
 *          and localStorage persistence.
 * WHY:     FR-0759 ("New Message popup should be wider/larger, structured").
 *          The dock was already tabbed + draggable at a FIXED 720×680; this
 *          lets operators resize it (and remembers their choice) without
 *          bloating ComposeDock.tsx with untestable inline math.
 * FLOW: pointer delta + stored size ──▶ clampDockSize ──▶ persisted DockSize
 */

export interface DockSize {
  width: number
  height: number
}

// The dock anchors bottom-right; these bound how small/large it can get.
// MIN keeps the recipients+subject+editor+Send usable; MAX stops it eating
// the whole screen on a big monitor (viewport clamp shrinks it further on
// laptops).
export const DOCK_MIN_W = 380
export const DOCK_MIN_H = 420
export const DOCK_MAX_W = 1100
export const DOCK_MAX_H = 920
export const DOCK_DEFAULT_W = 720
export const DOCK_DEFAULT_H = 680
export const DOCK_EDGE = 8

function clampTo(value: number, lo: number, hi: number): number {
  if (hi < lo) return lo
  return Math.min(Math.max(value, lo), hi)
}

/**
 * Clamp a desired size to [MIN, min(MAX, viewport − 2·edge)] on each axis.
 * Guarantees the dock never exceeds the viewport and never collapses below
 * the usable minimum.
 */
export function clampDockSize(
  size: DockSize,
  vw: number,
  vh: number,
  edge: number = DOCK_EDGE,
): DockSize {
  const maxW = Math.min(DOCK_MAX_W, Math.max(DOCK_MIN_W, vw - 2 * edge))
  const maxH = Math.min(DOCK_MAX_H, Math.max(DOCK_MIN_H, vh - 2 * edge))
  return {
    width: Math.round(clampTo(size.width, DOCK_MIN_W, maxW)),
    height: Math.round(clampTo(size.height, DOCK_MIN_H, maxH)),
  }
}

/**
 * Given the size at drag-start and the pointer delta, compute the new size
 * for a TOP-LEFT resize handle on a bottom-right-anchored dock: dragging the
 * handle left (dx < 0) or up (dy < 0) GROWS the dock. Caller clamps.
 */
export function resizeFromDelta(start: DockSize, dx: number, dy: number): DockSize {
  return {
    width: start.width - dx,
    height: start.height - dy,
  }
}

export function loadDockSize(
  key: string,
  storage: Pick<Storage, 'getItem'> | undefined = safeStorage(),
): DockSize | null {
  if (!storage) return null
  try {
    const raw = storage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<DockSize>
    if (typeof parsed?.width === 'number' && typeof parsed?.height === 'number') {
      return { width: parsed.width, height: parsed.height }
    }
  } catch {
    /* corrupt payload — fall back to default */
  }
  return null
}

export function saveDockSize(
  key: string,
  size: DockSize,
  storage: Pick<Storage, 'setItem'> | undefined = safeStorage(),
): void {
  if (!storage) return
  try {
    storage.setItem(key, JSON.stringify(size))
  } catch {
    /* quota / serialisation failure is non-fatal */
  }
}

function safeStorage(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined
  } catch {
    return undefined
  }
}
